import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { Column } from '../DataTable';
import EmptyState from '../EmptyState';
import { MetricCard } from '../MetricCards';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import StatusBadge from '../ui/StatusBadge';
import { RecordIdLabel, idSortKey } from '../ui/idColumn';
import { useNotifications } from '../NotificationProvider';
import ConfirmPackDialog from './ConfirmPackDialog';
import { useHeaderSearch } from '../../hooks/useHeaderSearch';
import { useLoad } from '../../hooks/useLoad';
import { usePageRefresh } from '../../hooks/usePageRefresh';
import { useRole } from '../../hooks/useRole';
import { ASSIGNMENTS_MARK_PACKED_EVENT, setAssignmentsBar, useAssignmentsBar } from '../../hooks/useAssignmentsBar';
import { bulkPack, fetchAssignments, fetchAssignmentSummary } from '../../services/AsoebiAssignmentService';
import {
  PACKING_LABELS, PACKING_VARIANT, assignmentMatches, fmtQuantity, packingOf, progressText,
} from '../../utils/asoebiAssignments';
import { entityPath } from '../../utils/entityRoutes';
import { errorMessageOf, isApiError } from '../../utils/apiErrors';
import type { AsoebiAssignment, AssignmentPacking, AssignmentSummary } from '../../types/ui/AsoebiAssignment';

const EMPTY_SUMMARY: AssignmentSummary = { total: 0, draft: 0, complete: 0, partial: 0, not_started: 0, lines_total: 0, lines_packed: 0 };
const NEW_PATH = '/asoebi/assignments/new';
const LIST_PATH = '/asoebi/assignments';
const stop = (e: React.MouseEvent) => e.stopPropagation();

/** Lines a bulk pack would pack: the unpacked lines of every chosen assignment. */
export const linesToPack = (rows: readonly AsoebiAssignment[]): number =>
  rows.reduce((sum, a) => sum + (a.total_count > 0 ? a.total_count - a.packed_count : 0), 0);

const AssignmentsList: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { canEdit } = useRole();
  const search = useHeaderSearch().trim().toLowerCase();
  // The Archived switch and the Packing filter live in the Header's bar (AssignmentsListBar); this list reads the same store.
  const { archived, packing: packingFilter } = useAssignmentsBar();
  const setPackingFilter = (packing: AssignmentPacking | '') => setAssignmentsBar({ packing });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const { data, loading, error, reload } = useLoad(
    async () => {
      const [rows, summary] = await Promise.all([fetchAssignments(archived), fetchAssignmentSummary(archived)]);
      return { rows, summary };
    },
    [archived],
  );
  usePageRefresh(reload, loading);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const summary = data?.summary ?? EMPTY_SUMMARY;
  const shown = useMemo(
    () => rows.filter(a => (!packingFilter || packingOf(a) === packingFilter) && assignmentMatches(a, search)),
    [rows, packingFilter, search],
  );
  const chosen = useMemo(() => rows.filter(a => selected.has(a.id)), [rows, selected]);

  // Ticks belong to the rows on screen: switching Active / Archived clears them.
  useEffect(() => { setSelected(new Set()); }, [archived]);
  useEffect(() => { setAssignmentsBar({ chosen: chosen.length }); }, [chosen.length]);
  useEffect(() => () => setAssignmentsBar({ chosen: 0 }), []);
  // Mark packed (a button in the bar) asks this list to confirm the ticked rows.
  useEffect(() => {
    const open = () => { setConfirmError(null); setConfirmOpen(true); };
    window.addEventListener(ASSIGNMENTS_MARK_PACKED_EVENT, open);
    return () => window.removeEventListener(ASSIGNMENTS_MARK_PACKED_EVENT, open);
  }, []);
  const toggleCard = (value: AssignmentPacking) => setPackingFilter(packingFilter === value ? '' : value);

  const confirmPack = async () => {
    setBusy(true);
    setConfirmError(null);
    try {
      const res = await bulkPack(chosen.map(a => a.id));
      setConfirmOpen(false);
      setSelected(new Set(res.results.filter(r => !r.ok).map(r => r.id)));
      if (res.failed > 0) {
        const first = res.results.find(r => !r.ok)?.error?.message;
        showError(`${res.failed} could not be packed${first ? `: ${first}` : '.'}${res.packed > 0 ? ` ${res.packed} packed.` : ''}`);
      } else {
        showSuccess(res.packed === 1 ? 'Assignment marked packed' : `${res.packed} assignments marked packed`);
      }
      await reload();
    } catch (err) {
      setConfirmError(errorMessageOf(err, 'Could not mark these packed. Refresh to see what changed before trying again.'));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    const forbidden = isApiError(error) && error.status === 403;
    return (
      <>
          <div className="wt-container fade-in">
          {forbidden
            ? <EmptyState icon="lock" title="You do not have access to asoebi packing" description="Ask the project owner to give you access." />
            : <LoadErrorBox what="assignments" error={error} onRetry={reload} retrying={loading} />}
        </div>
      </>
    );
  }

  const columns: Column<AsoebiAssignment>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '112px',
      sortable: true,
      className: 'rec-id-cell',
      sortValue: a => idSortKey('asoebiAssignment', a.id),
      render: (_v, a) => (
        <Link className="aa-link" to={entityPath('asoebiAssignment', a.id) ?? LIST_PATH} onClick={stop}>
          <RecordIdLabel kind="asoebiAssignment" id={a.id} />
        </Link>
      ),
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '112px',
      sortable: true,
      sortValue: a => idSortKey('fabricAssignment', a.order_id),
      render: (_v, a) => (
        <Link className="aa-link" to={entityPath('fabricAssignment', a.order_id) ?? '/asoebi/orders'} onClick={stop}>
          <RecordIdLabel kind="fabricAssignment" id={a.order_id} />
        </Link>
      ),
    },
    { key: 'guest_name', header: 'Guest', sortable: true, render: (_v, a) => a.guest_name ?? '—' },
    { key: 'fabric_name', header: 'Bundle / fabric', sortable: true, render: (_v, a) => a.fabric_name ?? '—' },
    { key: 'quantity', header: 'Sets', align: 'right', render: (_v, a) => fmtQuantity(a.quantity) },
    { key: 'packed_count', header: 'Packed', sortable: true, render: (_v, a) => progressText(a) },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: a => packingOf(a),
      render: (_v, a) => {
        const p = packingOf(a);
        return <StatusBadge variant={PACKING_VARIANT[p]}>{PACKING_LABELS[p]}</StatusBadge>;
      },
    },
  ];

  const nothingAtAll = !loading && rows.length === 0;
  const nothingShown = !loading && !nothingAtAll && shown.length === 0;

  return (
    <>
      <div className="wt-container fade-in aa-page">
        <section className="stats-grid" aria-label="Packing summary">
          <MetricCard label="Assignments" value={summary.total} tone="blue" icon="package" active={packingFilter === ''} onClick={() => setPackingFilter('')} />
          <MetricCard label="Not started" value={summary.not_started} tone="orange" icon="clock" active={packingFilter === 'not_started'} onClick={() => toggleCard('not_started')} />
          <MetricCard label="Part packed" value={summary.partial} tone="purple" icon="edit" active={packingFilter === 'partial'} onClick={() => toggleCard('partial')} />
          <MetricCard label="Packed" value={summary.complete} tone="green" icon="check" active={packingFilter === 'complete'} onClick={() => toggleCard('complete')} />
        </section>

        {nothingAtAll || nothingShown ? (
          <EmptyState
            icon="package"
            title={nothingAtAll ? (archived ? 'No archived assignments' : 'No assignments yet') : 'No assignments match'}
            description={nothingAtAll
              ? (archived ? undefined : 'An assignment says which bundle fills an asoebi order. Create one, then tick off each item as you pack it.')
              : 'Try another packing filter or a different search.'}
            action={nothingAtAll
              ? (!archived && canEdit ? <Button variant="primary" onClick={() => navigate(NEW_PATH)}>New assignment</Button> : undefined)
              : (packingFilter ? <Button variant="secondary" onClick={() => setPackingFilter('')}>Clear filter</Button> : undefined)}
          />
        ) : (
          <DataTable<AsoebiAssignment>
            recordList={{ kind: 'asoebiAssignment' }}
            data={shown}
            columns={columns}
            loading={loading}
            emptyMessage="No assignments"
            {...(canEdit && !archived
              ? { selectable: true, rowKey: (a: AsoebiAssignment) => a.id, selectedKeys: selected, onSelectedKeysChange: (keys: Set<string>) => setSelected(keys) }
              : {})}
            onRowClick={a => navigate(entityPath('asoebiAssignment', a.id) ?? LIST_PATH)}
          />
        )}
      </div>

      <ConfirmPackDialog
        open={confirmOpen}
        assignments={chosen.length}
        lines={linesToPack(chosen)}
        loading={busy}
        error={confirmError}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmPack}
      />
    </>
  );
};

export default AssignmentsList;
